import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/Layouts";
import { DashboardCard } from "@/components/DashboardCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { TradingChartLoader } from "@/components/TradingChartLoader";
import {
  ShieldAlert,
  Activity,
  Download,
  Monitor,
  Ban,
  Unlock,
  RotateCcw,
  FileCheck,
  Video,
  FileText,
  Users as UsersIcon,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Pagination } from "@/components/Pagination";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-MY", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

function shortUA(ua = "") {
  if (!ua) return "—";
  if (/iPhone|iPad/.test(ua)) return "iOS Safari";
  if (/Android/.test(ua)) return "Android";
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua)) return "Safari";
  return ua.slice(0, 40) + (ua.length > 40 ? "…" : "");
}

export default function AdminSecurity() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [kind, setKind] = useState("all");
  const [devices, setDevices] = useState([]);
  const [suspicious, setSuspicious] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pagination — separate trackers for the audit-log and device tables
  const [logPage, setLogPage] = useState(1);
  const [logPageSize, setLogPageSize] = useState(25);
  const [devicePage, setDevicePage] = useState(1);
  const [devicePageSize, setDevicePageSize] = useState(25);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l, d, sus] = await Promise.all([
        api.get("/admin/security/stats").then((r) => r.data),
        api.get(`/admin/security/audit-logs?kind=${kind}`).then((r) => r.data),
        api.get("/admin/security/devices").then((r) => r.data),
        api.get("/admin/security/suspicious").then((r) => r.data),
      ]);
      setStats(s);
      setLogs(l);
      setDevices(d);
      setSuspicious(sus);
    } catch {
      toast.error("Failed to load security data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const resetDevices = async (userId) => {
    if (!window.confirm("Reset semua peranti pengguna ini?")) return;
    try {
      await api.post(`/admin/security/users/${userId}/reset-devices`);
      toast.success("Peranti di-reset");
      load();
    } catch { toast.error("Gagal reset"); }
  };

  const blockContent = async (userId) => {
    if (!window.confirm("Block content access for this user?")) return;
    try {
      await api.post(`/admin/security/users/${userId}/block-content`);
      toast.success("Access blocked");
      load();
    } catch { toast.error("Gagal sekat"); }
  };

  const unblockContent = async (userId) => {
    try {
      await api.post(`/admin/security/users/${userId}/unblock-content`);
      toast.success("Access unblocked");
      load();
    } catch { toast.error("Failed to unblock access"); }
  };

  const markReviewed = async (logId) => {
    try {
      await api.post(`/admin/security/audit-logs/${logId}/mark-reviewed`);
      toast.success("Marked as reviewed");
      load();
    } catch { toast.error("Gagal"); }
  };

  const exportCSV = () => {
    const headers = ["Time", "Type", "User", "Email", "Resource", "IP", "Device", "Status", "Suspicious"];
    const lines = [headers.join(",")];
    logs.forEach((l) => {
      lines.push([
        fmtTime(l.started_at),
        l.type,
        l.user_name,
        l.user_email,
        l.resource_title,
        l.ip_address,
        shortUA(l.user_agent),
        l.status,
        l.suspicious ? "yes" : "no",
      ].map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `security-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV dieksport");
  };

  if (loading && !stats) {
    return <AdminLayout title="Security"><div className="flex items-center justify-center py-16"><TradingChartLoader size="lg" text="Loading security panel..." /></div></AdminLayout>;
  }

  return (
    <AdminLayout title="Security">
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-2">
          <ShieldAlert className="h-7 w-7 text-primary" /> Security Monitoring
        </h1>
        <p className="mt-1 text-muted-foreground">
          Monitor video playback, PDF downloads, devices, and suspicious activity.
        </p>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <DashboardCard icon={Video} label="Video Plays (24h)" value={stats?.plays_24h ?? 0} accent="teal" testId="sec-stat-plays" />
        <DashboardCard icon={Download} label="PDF Downloads (24h)" value={stats?.downloads_24h ?? 0} accent="teal" testId="sec-stat-downloads" />
        <DashboardCard icon={Monitor} label="Active Devices" value={stats?.total_devices ?? 0} accent="navy" testId="sec-stat-devices" />
        <DashboardCard icon={ShieldAlert} label="Suspicious" value={stats?.suspicious_events ?? 0} accent="gold" testId="sec-stat-sus" />
        <DashboardCard icon={Ban} label="Blocked Users" value={stats?.blocked_users ?? 0} accent="danger" testId="sec-stat-blocked" />
      </div>

      {/* Suspicious users alert */}
      {suspicious.length > 0 && (
        <Card className="glass-card border-destructive/40 mb-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-destructive/15 via-transparent to-transparent" />
          <CardContent className="p-5 relative">
            <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
              <ShieldAlert className="h-4.5 w-4.5 text-destructive" />
              {suspicious.length} users with suspicious activity
            </h3>
            <div className="mt-4 space-y-2">
              {suspicious.map((s) => (
                <div key={s.user.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card/50 p-3 flex-wrap" data-testid={`sus-${s.user.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{s.user.name} <span className="text-xs text-muted-foreground">· {s.user.email}</span></p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {s.flags.map((f) => f.type.replace(/_/g, " ")).join(" • ")}
                      {" · "}
                      Plays: {s.recent_plays} · IPs: {s.unique_ips} · Devices: {s.device_count}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => resetDevices(s.user.id)} data-testid={`sus-reset-${s.user.id}`}>
                      <RotateCcw className="h-3.5 w-3.5" /> Reset Devices
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => blockContent(s.user.id)} data-testid={`sus-block-${s.user.id}`}>
                      <Ban className="h-3.5 w-3.5" /> Block
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="logs">
        <TabsList>
          <TabsTrigger value="logs" data-testid="tab-logs"><Activity className="h-4 w-4" /> Audit Logs</TabsTrigger>
          <TabsTrigger value="devices" data-testid="tab-devices"><Monitor className="h-4 w-4" /> Devices</TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="mt-5 space-y-3">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex gap-2">
              <Button size="sm" variant={kind === "all" ? "default" : "outline"} onClick={() => setKind("all")} data-testid="filter-all">All</Button>
              <Button size="sm" variant={kind === "video" ? "default" : "outline"} onClick={() => setKind("video")} data-testid="filter-video"><Video className="h-3.5 w-3.5" /> Video</Button>
              <Button size="sm" variant={kind === "pdf" ? "default" : "outline"} onClick={() => setKind("pdf")} data-testid="filter-pdf"><FileText className="h-3.5 w-3.5" /> PDF</Button>
            </div>
            <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-sec-csv"><Download className="h-3.5 w-3.5" /> Export CSV</Button>
          </div>
          <Card className="glass-card">
            <CardContent className="p-0">
              <div className="table-wrap">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Resource</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>Browser</TableHead>
                      <TableHead>Flag</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No logs at this time.</TableCell></TableRow>
                    ) : logs.slice((Math.min(logPage, Math.max(1, Math.ceil(logs.length / logPageSize))) - 1) * logPageSize, Math.min(logPage, Math.max(1, Math.ceil(logs.length / logPageSize))) * logPageSize).map((l) => (
                      <TableRow key={l.id} data-testid={`log-row-${l.id}`}>
                        <TableCell className="font-mono text-xs whitespace-nowrap">{fmtTime(l.started_at)}</TableCell>
                        <TableCell>
                          {l.type === "video" ? (
                            <StatusBadge status="percuma" label="VIDEO" />
                          ) : (
                            <StatusBadge status="berbayar" label="PDF" />
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm font-medium">{l.user_name}</p>
                            <p className="text-xs text-muted-foreground">{l.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[180px] truncate">{l.resource_title}</TableCell>
                        <TableCell className="font-mono text-xs">{l.ip_address}</TableCell>
                        <TableCell className="text-xs">{shortUA(l.user_agent)}</TableCell>
                        <TableCell>
                          {l.suspicious ? <StatusBadge status="expired" label="Sus" /> : <StatusBadge status="active" label="OK" />}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {l.suspicious && (
                            <Button size="sm" variant="ghost" onClick={() => markReviewed(l.id)} title="Mark reviewed"><FileCheck className="h-3.5 w-3.5" /></Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Pagination
                page={Math.min(logPage, Math.max(1, Math.ceil(logs.length / logPageSize)))}
                pageSize={logPageSize}
                total={logs.length}
                onPageChange={setLogPage}
                onPageSizeChange={setLogPageSize}
                testId="logs-pagination"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="devices" className="mt-5">
          <Card className="glass-card">
            <CardContent className="p-0">
              <div className="table-wrap">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Browser / Device</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>First Seen</TableHead>
                      <TableHead>Last Seen</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {devices.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No registered devices.</TableCell></TableRow>
                    ) : devices.slice((Math.min(devicePage, Math.max(1, Math.ceil(devices.length / devicePageSize))) - 1) * devicePageSize, Math.min(devicePage, Math.max(1, Math.ceil(devices.length / devicePageSize))) * devicePageSize).map((d) => (
                      <TableRow key={`${d.user_id}-${d.device_id}`} data-testid={`device-row-${d.device_id}`}>
                        <TableCell>
                          <p className="text-sm font-medium">{d.user_name}</p>
                          <p className="text-xs text-muted-foreground">{d.user_email}</p>
                        </TableCell>
                        <TableCell className="text-xs">{shortUA(d.user_agent)}</TableCell>
                        <TableCell className="font-mono text-xs">{d.ip_address}</TableCell>
                        <TableCell className="font-mono text-xs whitespace-nowrap">{fmtTime(d.first_seen)}</TableCell>
                        <TableCell className="font-mono text-xs whitespace-nowrap">{fmtTime(d.last_seen)}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => resetDevices(d.user_id)} title="Reset all devices for this user">
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => blockContent(d.user_id)} title="Block content access">
                            <Ban className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => unblockContent(d.user_id)} title="Unblock content access">
                            <Unlock className="h-3.5 w-3.5 text-teal" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Pagination
                page={Math.min(devicePage, Math.max(1, Math.ceil(devices.length / devicePageSize)))}
                pageSize={devicePageSize}
                total={devices.length}
                onPageChange={setDevicePage}
                onPageSizeChange={setDevicePageSize}
                testId="devices-pagination"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
